import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  arrayRemove,
  arrayUnion,
  doc,
  updateDoc,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";

import { Avatar } from "@/components/ui/Avatar";
import { Card } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useTranslations } from "@/contexts/I18nContext";
import { useFirestoreCollection, useFirestoreDoc } from "@/hooks/useFirestore";
import { db, storage } from "@/lib/firebase";
import { fromTimestamp } from "@/lib/firestore";
import { relativeDate } from "@/lib/humanDate";
import { DIETARY_RESTRICTIONS } from "@/lib/constants";
import {
  buildMemberChoresQuery,
  buildMemberPostsQuery,
  buildMemberResponsibilitiesQuery,
  memberTypeBadgeKey,
  selectMemberChores,
  selectMemberPosts,
  selectMemberResponsibilities,
} from "@/lib/memberQueries";
import { removeMemberFromHousehold } from "@/lib/memberRemove";
import { MemberEditModal } from "@/components/features/MemberEditModal";
import type {
  AssignedChore,
  HouseholdPost,
  MemberProfile,
  Responsibility,
} from "@/lib/models";

type Tab = "activity" | "info" | "medical";

export function MemberDetailPage() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();

  const {
    profile,
    household,
    householdId,
    loading: contextLoading,
  } = useHouseholdContext();

  const t = useTranslations();

  // Fetch the member profile
  const { data: member, loading: memberLoading } =
    useFirestoreDoc<MemberProfile>("member_profiles", memberId ?? null);

  // Build the four query specs (3 activity + the member doc above).
  const postsSpec = useMemo(
    () => buildMemberPostsQuery(householdId, memberId ?? null),
    [householdId, memberId],
  );
  const respSpec = useMemo(
    () => buildMemberResponsibilitiesQuery(householdId, memberId ?? null),
    [householdId, memberId],
  );
  const choresSpec = useMemo(
    () => buildMemberChoresQuery(memberId ?? null),
    [memberId],
  );

  // Subscribe in parallel — useFirestoreCollection accepts a null path.
  const { data: posts, loading: postsLoading } =
    useFirestoreCollection<HouseholdPost>(postsSpec?.path ?? null);
  const { data: responsibilities, loading: respLoading } =
    useFirestoreCollection<Responsibility>(respSpec?.path ?? null);
  const { data: chores, loading: choresLoading } =
    useFirestoreCollection<AssignedChore>(choresSpec?.path ?? null);

  // Apply pure client-side filter+sort+slice.
  const memberPosts = useMemo(
    () => (memberId ? selectMemberPosts(posts, memberId) : []),
    [posts, memberId],
  );
  const memberResponsibilities = useMemo(
    () =>
      memberId ? selectMemberResponsibilities(responsibilities, memberId) : [],
    [responsibilities, memberId],
  );
  const memberChores = useMemo(() => selectMemberChores(chores), [chores]);

  // Local UI state
  const [tab, setTab] = useState<Tab>("activity");
  const [editingNickname, setEditingNickname] = useState(false);
  const [nicknameDraft, setNicknameDraft] = useState("");
  const [savingNickname, setSavingNickname] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isSelf = !!profile?.uid && profile.uid === memberId;
  const isHouseholdOwner =
    !!profile?.uid && household?.household_owner === profile.uid;
  const canEditAvatar = isSelf || isHouseholdOwner;

  // Keep the nickname draft in sync with the loaded profile when the user
  // first opens the editor.
  useEffect(() => {
    if (editingNickname) {
      setNicknameDraft(member?.nickname ?? member?.name ?? "");
    }
  }, [editingNickname, member?.nickname, member?.name]);

  if (contextLoading || memberLoading) {
    return <Spinner />;
  }

  if (!member) {
    return (
      <div className="p-4 space-y-4">
        <BackToAccount
          label={t("members.detail.backToAccount")}
          onBack={() => navigate("/account")}
        />
        <Card>
          <p className="text-sm text-gray-500 text-center py-6">
            {t("members.detail.notFound")}
          </p>
        </Card>
      </div>
    );
  }

  const displayName =
    member.nickname || member.name || t("members.detail.unknownMember");

  const handleSaveNickname = async () => {
    if (!isSelf || !memberId) return;
    setSavingNickname(true);
    try {
      const profileRef = doc(db, "member_profiles", memberId);
      await updateDoc(profileRef, { nickname: nicknameDraft.trim() });
      setEditingNickname(false);
    } catch (error) {
      console.error("Error saving nickname:", error);
    } finally {
      setSavingNickname(false);
    }
  };

  const handleAvatarUpload = async (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    if (!file || !memberId || !canEditAvatar) return;

    setUploadingAvatar(true);
    try {
      const { compressForAvatar } = await import("@/lib/imageCompressor");
      const compressed = await compressForAvatar(file);
      const storageRef = ref(storage, `member_profiles/${memberId}/avatar.jpg`);
      await uploadBytes(storageRef, compressed);
      const url = await getDownloadURL(storageRef);

      const profileRef = doc(db, "member_profiles", memberId);
      await updateDoc(profileRef, { profile_url: url });
    } catch (error) {
      console.error("Error uploading avatar:", error);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const toggleDietary = async (restriction: string) => {
    if (!isSelf || !memberId) return;
    const profileRef = doc(db, "member_profiles", memberId);
    const current = member.dietary_restrictions ?? [];
    if (current.includes(restriction)) {
      await updateDoc(profileRef, {
        dietary_restrictions: arrayRemove(restriction),
      });
    } else {
      await updateDoc(profileRef, {
        dietary_restrictions: arrayUnion(restriction),
      });
    }
  };

  const activityLoading = postsLoading || respLoading || choresLoading;
  const noActivity =
    !activityLoading &&
    memberPosts.length === 0 &&
    memberResponsibilities.length === 0 &&
    memberChores.length === 0;

  return (
    <div className="p-4 space-y-5">
      <BackToAccount
        label={t("members.detail.backToAccount")}
        onBack={() => navigate("/account")}
      />

      {/* Header card */}
      <Card>
        <div className="flex items-center gap-4">
          <div className="relative">
            <button
              onClick={() => canEditAvatar && fileInputRef.current?.click()}
              className={`relative ${canEditAvatar ? "group cursor-pointer" : "cursor-default"}`}
              disabled={!canEditAvatar || uploadingAvatar}
              aria-label={
                canEditAvatar
                  ? t("members.detail.saveAvatar")
                  : displayName
              }
            >
              <Avatar src={member.profile_url} alt={displayName} size={80} />
              {canEditAvatar && (
                <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium">
                    {t("members.detail.editAvatar")}
                  </span>
                </div>
              )}
              {uploadingAvatar && (
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                  <div
                    className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: "white", borderTopColor: "transparent" }}
                  />
                </div>
              )}
            </button>
            {canEditAvatar && (
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleAvatarUpload}
                className="hidden"
              />
            )}
          </div>

          <div className="flex-1 min-w-0">
            {editingNickname && isSelf ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={nicknameDraft}
                  onChange={(e) => setNicknameDraft(e.target.value)}
                  autoFocus
                  className="flex-1 px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void handleSaveNickname();
                    if (e.key === "Escape") setEditingNickname(false);
                  }}
                />
                <button
                  onClick={handleSaveNickname}
                  disabled={savingNickname}
                  className="text-sm font-medium shrink-0"
                  style={{ color: "var(--accent-color, #00C7BE)" }}
                >
                  {savingNickname
                    ? t("account.nickname.saving")
                    : t("account.nickname.save")}
                </button>
              </div>
            ) : (
              <button
                onClick={() => isSelf && setEditingNickname(true)}
                className={`text-left ${isSelf ? "group" : "cursor-default"}`}
                disabled={!isSelf}
              >
                <p
                  className={`font-bold text-lg leading-tight ${isSelf ? "group-hover:underline" : ""}`}
                >
                  {displayName}
                </p>
              </button>
            )}

            <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
              <span
                className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full text-white capitalize"
                style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
              >
                {t(memberTypeBadgeKey(member.memberType))}
              </span>
              {isSelf && (
                <span className="text-xs text-gray-500">
                  {t("members.detail.youBadge")}
                </span>
              )}
            </div>
          </div>

          {/* Edit + remove affordances — Edit is available to self
              and to the household owner; Remove is owner-only and
              hidden for self. Mirrors iOS MemberCard:367-385. */}
          {(isSelf || isHouseholdOwner) && (
            <div className="flex flex-col gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => setShowEditModal(true)}
                className="px-3 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors"
              >
                {t("members.detail.editMember")}
              </button>
              {!isSelf && isHouseholdOwner && (
                <button
                  type="button"
                  onClick={() => setShowRemoveConfirm(true)}
                  className="px-3 py-1 rounded-lg text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                >
                  {t("members.detail.removeMember")}
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Tab picker */}
      <div className="flex rounded-lg bg-gray-100 dark:bg-white/5 p-1 text-sm font-medium">
        <TabButton current={tab} value="activity" onClick={setTab}>
          {t("members.detail.tabs.activity")}
        </TabButton>
        <TabButton current={tab} value="info" onClick={setTab}>
          {t("members.detail.tabs.info")}
        </TabButton>
        <TabButton current={tab} value="medical" onClick={setTab}>
          {t("members.detail.tabs.medical")}
        </TabButton>
      </div>

      {/* Activity */}
      {tab === "activity" && (
        <div className="space-y-4">
          {activityLoading && <Spinner small />}

          {/* Posts */}
          {memberPosts.length > 0 && (
            <ActivitySection
              title={t("members.detail.activity.posts")}
              seeMoreHref="/posts"
              seeMoreLabel={t("members.detail.activity.seeMore")}
            >
              {memberPosts.map((post) => (
                <Link
                  key={post.uid}
                  to={`/posts/${post.uid}`}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <span aria-hidden className="text-base">{"\u{1F4AC}"}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{post.post_title}</p>
                    {fromTimestamp(post.created_at) && (
                      <p className="text-xs text-gray-400">
                        {relativeDate(fromTimestamp(post.created_at) as Date)}
                      </p>
                    )}
                  </div>
                  <Chevron />
                </Link>
              ))}
            </ActivitySection>
          )}

          {/* Responsibilities */}
          {memberResponsibilities.length > 0 && (
            <ActivitySection
              title={t("members.detail.activity.responsibilities")}
              seeMoreHref="/responsibilities"
              seeMoreLabel={t("members.detail.activity.seeMore")}
            >
              {memberResponsibilities.map((r) => (
                <Link
                  key={r.uid}
                  to="/responsibilities"
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <span aria-hidden className="text-base">{"✅"}</span>
                  <p className="text-sm flex-1 truncate">{r.title}</p>
                  <Chevron />
                </Link>
              ))}
            </ActivitySection>
          )}

          {/* Chores */}
          {memberChores.length > 0 && (
            <ActivitySection
              title={t("members.detail.activity.chores")}
              seeMoreHref="/chores"
              seeMoreLabel={t("members.detail.activity.seeMore")}
            >
              {memberChores.map((chore) => (
                <Link
                  key={chore.uid}
                  to="/chores"
                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <span aria-hidden className="text-base">{"\u{1F9F9}"}</span>
                  <p className="text-sm flex-1 truncate">{chore.title}</p>
                  <Chevron />
                </Link>
              ))}
            </ActivitySection>
          )}

          {noActivity && (
            <Card>
              <EmptyState
                icon="\u{1F4ED}"
                title={t("members.detail.activity.empty")}
              />
            </Card>
          )}
        </div>
      )}

      {/* Info */}
      {tab === "info" && (
        <Card>
          <div className="space-y-3">
            <h2 className="text-sm font-semibold text-gray-500">
              {t("members.detail.info.title")}
            </h2>
            <InfoRow label={t("members.detail.info.name")} value={member.name} />
            <InfoRow
              label={t("members.detail.info.nickname")}
              value={member.nickname}
              action={
                isSelf ? (
                  <button
                    onClick={() => setEditingNickname(true)}
                    className="text-sm font-medium"
                    style={{ color: "var(--accent-color, #00C7BE)" }}
                  >
                    {t("members.detail.editNickname")}
                  </button>
                ) : undefined
              }
            />
            <InfoRow
              label={t("members.detail.info.notes")}
              value={member.notes}
            />
            <InfoRow
              label={t("members.detail.info.location")}
              value={member.locationStatus}
            />
          </div>
        </Card>
      )}

      {/* Medical */}
      {tab === "medical" && (
        <div className="space-y-4">
          <Card>
            <h2 className="text-sm font-semibold text-gray-500 mb-3">
              {t("members.detail.medical.title")}
            </h2>

            {/* Allergies */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                {t("account.allergies")}
              </p>
              {member.allergies && member.allergies.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {member.allergies.map((allergy) => (
                    <span
                      key={allergy}
                      className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                    >
                      {allergy}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  {t("members.detail.medical.noneListed")}
                </p>
              )}
            </div>

            {/* Dietary restrictions */}
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {t("account.dietary.title")}
              </p>
              {isSelf ? (
                <div className="flex flex-wrap gap-2">
                  {DIETARY_RESTRICTIONS.map((restriction) => {
                    const isSelected = (
                      member.dietary_restrictions ?? []
                    ).includes(restriction);
                    return (
                      <button
                        key={restriction}
                        onClick={() => toggleDietary(restriction)}
                        className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                          isSelected
                            ? "bg-[color:var(--accent-color)] text-white"
                            : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15"
                        }`}
                      >
                        {restriction}
                      </button>
                    );
                  })}
                </div>
              ) : member.dietary_restrictions &&
                member.dietary_restrictions.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {member.dietary_restrictions.map((r) => (
                    <span
                      key={r}
                      className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300"
                    >
                      {r}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">
                  {t("members.detail.medical.noneListed")}
                </p>
              )}
            </div>
          </Card>
        </div>
      )}

      {showEditModal && memberId && (
        <MemberEditModal
          member={member}
          memberId={memberId}
          canEditType={isHouseholdOwner}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {showRemoveConfirm && memberId && householdId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => !removing && setShowRemoveConfirm(false)}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 p-5 shadow-xl text-gray-900 dark:text-gray-100">
            <h3 className="font-bold text-base">
              {t("members.removeConfirm.title", { name: displayName })}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {member.memberType === "pet"
                ? t("members.removeConfirm.petBody")
                : t("members.removeConfirm.humanBody")}
            </p>
            <div className="mt-4 flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowRemoveConfirm(false)}
                disabled={removing}
                className="px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-gray-100 dark:hover:bg-white/5 disabled:opacity-50"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!memberId || !householdId) return;
                  setRemoving(true);
                  try {
                    await removeMemberFromHousehold({
                      householdId,
                      memberId,
                      isPet: member.memberType === "pet",
                    });
                    navigate("/members");
                  } catch (err) {
                    console.error("Failed to remove member:", err);
                    setRemoving(false);
                  }
                }}
                disabled={removing}
                className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
              >
                {removing
                  ? t("members.removeConfirm.removing")
                  : t("members.removeConfirm.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}

// ---------- Local building blocks ----------

function BackToAccount({
  label,
  onBack,
}: {
  label: string;
  onBack: () => void;
}) {
  return (
    <button
      onClick={onBack}
      className="flex items-center gap-1 text-sm font-medium"
      style={{ color: "var(--accent-color, #00C7BE)" }}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
      {label}
    </button>
  );
}

function Spinner({ small = false }: { small?: boolean }) {
  return (
    <div className={`flex items-center justify-center ${small ? "py-4" : "h-64"}`}>
      <div
        className={`${small ? "w-5 h-5 border-2" : "w-8 h-8 border-3"} border-t-transparent rounded-full animate-spin`}
        style={{
          borderColor: "var(--accent-color, #00C7BE)",
          borderTopColor: "transparent",
        }}
      />
    </div>
  );
}

function TabButton({
  current,
  value,
  onClick,
  children,
}: {
  current: Tab;
  value: Tab;
  onClick: (t: Tab) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      className={`flex-1 rounded-md py-1.5 transition-colors ${
        active
          ? "bg-white dark:bg-gray-700 shadow-sm"
          : "text-gray-500 dark:text-gray-400"
      }`}
    >
      {children}
    </button>
  );
}

function ActivitySection({
  title,
  seeMoreHref,
  seeMoreLabel,
  children,
}: {
  title: string;
  seeMoreHref: string;
  seeMoreLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Card padding="p-0">
      <div className="flex items-center justify-between px-3 pt-3 pb-1">
        <p className="text-sm font-semibold text-gray-500">{title}</p>
        <Link
          to={seeMoreHref}
          className="text-xs font-medium"
          style={{ color: "var(--accent-color, #00C7BE)" }}
        >
          {seeMoreLabel}
        </Link>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-white/5">
        {children}
      </div>
    </Card>
  );
}

function InfoRow({
  label,
  value,
  action,
}: {
  label: string;
  value?: string | null;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
          {label}
        </p>
        <p className="text-sm mt-0.5">
          {value && value.trim().length > 0 ? value : "—"}
        </p>
      </div>
      {action}
    </div>
  );
}

function Chevron() {
  return (
    <svg
      className="text-gray-300 shrink-0"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}
